import { useCallback } from 'react';
import type { GuestBookingPricingItemResource } from '@anyroad/api';
import type { RescheduleMutationType, PaymentDetailsResult } from '../types';
import { parseAmountDue } from '../helpers';

type UsePricingDiffHandlerParams = {
  rescheduleMutation: RescheduleMutationType;
  diffMutationContext: { date: string; time: string } | null;
  setPaymentDetailsResult: (result: PaymentDetailsResult) => void;
  setStep: (step: 'payment-details' | 'booking-updated') => void;
};

export const usePricingDiffHandler = ({
  rescheduleMutation,
  diffMutationContext,
  setPaymentDetailsResult,
  setStep,
}: UsePricingDiffHandlerParams) => {
  return useCallback(
    (
      data: GuestBookingPricingItemResource,
      variables: {
        guestBookingId: string;
        data: { time_slot_id: number };
      }
    ) => {
      if (!diffMutationContext) return;

      const amountDue = parseAmountDue(data.amount_due ?? '');

      if (amountDue > 0) {
        setPaymentDetailsResult({ type: 'amount-due', pricing: data });
        setStep('payment-details');
      } else if (amountDue < 0) {
        setPaymentDetailsResult({ type: 'refund', pricing: data });
        setStep('payment-details');
      } else {
        rescheduleMutation.mutate({
          bookingId: variables.guestBookingId,
          data: {
            booking: {
              date: diffMutationContext.date,
              time: diffMutationContext.time,
            },
          },
        });
      }
    },
    [diffMutationContext, rescheduleMutation, setPaymentDetailsResult, setStep]
  );
};
